import { useContext } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, OverlayTrigger, Popover, ProgressBar, Tooltip } from '@openedx/paragon';
import { RiArrowLeftSLine, RiLineChartLine } from '@remixicon/react';
import { Link } from 'react-router-dom';
import { fetchCourseEngagement } from '@blend-ed/blendxplatform/api';

import messages from '../components/messages';
import {
  LEVEL_CLASSES,
  aggregateTotalsByIndex,
  normalizeEngagement,
  formatTooltipDate,
  getWeekdayShortNames,
} from '../components/activityMapUtils';

const DAYS_IN_YEAR = 365;
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun
const WEEKDAY_ROW_INDEXES = [1, 3, 5]; // Mon, Wed, Fri (Date.getDay() order)
const MONTHLY_SUMMARY_COUNT = 6;

const buildWeekdayTotals = (totalsByIndex, startDate) => {
  const start = new Date(startDate);
  const byWeekday = [0, 0, 0, 0, 0, 0, 0];
  totalsByIndex.forEach((val, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    byWeekday[d.getDay()] += val || 0;
  });
  return byWeekday;
};

const buildMonthlyTotals = (totalsByIndex, startDate, locale) => {
  const start = new Date(startDate);
  const byMonth = new Map();
  totalsByIndex.forEach((val, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const prev = byMonth.get(key) || { units: 0, activeDays: 0 };
    byMonth.set(key, {
      units: prev.units + (val || 0),
      activeDays: prev.activeDays + (val > 0 ? 1 : 0),
    });
  });
  const now = new Date();
  const rows = [];
  for (let i = 0; i < MONTHLY_SUMMARY_COUNT; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const prev = new Date(now.getFullYear(), now.getMonth() - i - 1, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const prevKey = `${prev.getFullYear()}-${prev.getMonth()}`;
    const stats = byMonth.get(key) || { units: 0, activeDays: 0 };
    const prevStats = byMonth.get(prevKey) || { units: 0, activeDays: 0 };
    const units = Math.round(stats.units);
    const prevUnits = Math.round(prevStats.units);
    let deltaPercent = null;
    if (prevUnits > 0) {
      deltaPercent = Math.round(((units - prevUnits) / prevUnits) * 100);
    } else if (units > 0) {
      deltaPercent = 100;
    }
    rows.push({
      key,
      name: d.toLocaleString(locale || undefined, { month: 'long' }),
      units,
      activeDays: stats.activeDays,
      deltaPercent,
      isCurrent: i === 0,
    });
  }
  return rows;
};

const buildWeeks = (days, startDate) => {
  // Pad leading cells so the first column starts on Sunday.
  const leadPad = new Date(startDate).getDay();
  const padded = Array(leadPad).fill(null).concat(days);
  while (padded.length % 7 !== 0) padded.push(null);

  const weeks = [];
  for (let i = 0; i < padded.length; i += 7) {
    weeks.push(padded.slice(i, i + 7));
  }
  return weeks;
};

const buildMonthLabels = (weeks, locale) => {
  const labels = [];
  let lastMonth = null;
  weeks.forEach((week, weekIndex) => {
    const firstDay = week.find((d) => d !== null);
    if (!firstDay) return;
    const month = new Date(firstDay.date).getMonth();
    if (month !== lastMonth) {
      labels.push({
        name: new Date(firstDay.date).toLocaleString(locale || undefined, { month: 'short' }),
        weekIndex,
      });
      lastMonth = month;
    }
  });
  return labels;
};

const DetailedActivityMap = () => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId || authenticatedUser?.id || authenticatedUser?.user_id || null;

  const endDateObj = new Date();
  const startDateObj = new Date(endDateObj);
  startDateObj.setDate(startDateObj.getDate() - (DAYS_IN_YEAR - 1));
  const startDate = startDateObj.toISOString().slice(0, 10);
  const endDate = endDateObj.toISOString().slice(0, 10);

  const { data: engagementData } = useQuery({
    queryKey: ['courseEngagement', userId, startDate, endDate],
    queryFn: () => fetchCourseEngagement(userId, startDate, endDate),
    enabled: !!userId,
  });

  const { locale } = intl;
  const days = normalizeEngagement(engagementData, startDate, DAYS_IN_YEAR);
  const weeks = buildWeeks(days, startDate);
  const monthLabels = buildMonthLabels(weeks, locale);
  const startYear = startDateObj.getFullYear();
  const endYear = endDateObj.getFullYear();

  const weekdayShortNames = getWeekdayShortNames(locale); // Date.getDay()-indexed
  const weekdayRowLabels = WEEKDAY_ROW_INDEXES.map((i) => weekdayShortNames[i]);
  const weekdayBarLabels = weekdayShortNames.map((name) => name.toUpperCase());

  const totalsByIndex = aggregateTotalsByIndex(engagementData);
  const weekdayTotals = buildWeekdayTotals(totalsByIndex, startDate);
  const maxWeekdayTotal = Math.max(...weekdayTotals, 1);
  const monthlyRows = buildMonthlyTotals(totalsByIndex, startDate, locale);

  return (
    <div className="progress-page activity-map__detail-page">
      <div className="activity-map__detail-header">
        <Button
          variant="tertiary"
          size="sm"
          as={Link}
          to="/progress"
          iconBefore={RiArrowLeftSLine}
        >
          {intl.formatMessage(messages.back)}
        </Button>
      </div>

      <div className="activity-map__section-header">
        <h2 className="activity-map__section-title">
          {intl.formatMessage(messages.detailedMap)}
        </h2>
      </div>

      <div className="activity-map__card">
        <span className="activity-map__subtitle">
          {intl.formatMessage(messages.lastYearActivity)}
        </span>

        <div className="activity-map__year-body">
          <div className="activity-map__weekday-labels">
            {weekdayRowLabels.map((label) => (
              <span key={label} className="activity-map__month-label">
                {label}
              </span>
            ))}
          </div>

          <div className="activity-map__year-grid-wrapper">
            <div className="activity-map__year-months">
              {monthLabels.map((label) => (
                <span
                  key={`${label.name}-${label.weekIndex}`}
                  className="activity-map__month-label activity-map__year-month-label"
                  style={{ left: `calc(${label.weekIndex} * (13px + 4px))` }}
                >
                  {label.name}
                </span>
              ))}
            </div>
            <div className="activity-map__year-grid">
            {weeks.map((week, wIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={wIdx} className="activity-map__week-col">
                {week.map((day, dIdx) => {
                  if (!day) {
                    return (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`empty-${wIdx}-${dIdx}`}
                        className="activity-map__cell activity-map__cell--sm activity-map__cell--empty"
                      />
                    );
                  }
                  return (
                    <OverlayTrigger
                      key={day.date}
                      placement="top"
                      overlay={(
                        <Tooltip id={`tooltip-${day.date}`}>
                          {formatTooltipDate(day.date, locale)}
                        </Tooltip>
                      )}
                    >
                      <div
                        className={`activity-map__cell activity-map__cell--sm ${LEVEL_CLASSES[day.level]}`}
                      />
                    </OverlayTrigger>
                  );
                })}
              </div>
            ))}
            </div>
            <div className="activity-map__year-range">
              <span className="activity-map__month-label">{startYear}</span>
              <span className="activity-map__month-label">{endYear}</span>
            </div>
          </div>
        </div>
        {/* Legend sits outside the horizontally-scrolling year grid so it stays put. */}
        <div className="activity-map__legend">
          <span className="activity-map__month-label">
            {intl.formatMessage(messages.less)}
          </span>
          <div className="activity-map__cell activity-map__cell--sm activity-map__cell--level-0" />
          <div className="activity-map__cell activity-map__cell--sm activity-map__cell--level-1" />
          <div className="activity-map__cell activity-map__cell--sm activity-map__cell--level-2" />
          <div className="activity-map__cell activity-map__cell--sm activity-map__cell--level-4" />
          <span className="activity-map__month-label">
            {intl.formatMessage(messages.more)}
          </span>
        </div>
      </div>

      <div className="activity-map__detail-row">
        <div className="activity-map__detail-col">
          <h2 className="activity-map__section-title">
            {intl.formatMessage(messages.mostActiveDays)}
          </h2>
          <div className="activity-map__card">
            <div className="active-days__list">
              {WEEKDAY_ORDER.map((weekdayIdx) => {
                const value = Math.round(weekdayTotals[weekdayIdx]);
                const pct = (value / maxWeekdayTotal) * 100;
                return (
                  <div key={weekdayIdx} className="active-days__row">
                    <span className="active-days__label">
                      {weekdayBarLabels[weekdayIdx]}
                    </span>
                    <ProgressBar
                      now={pct}
                      label={`${value}`}
                      srOnly
                      variant="primary-700"
                      size="lg"
                      className="active-days__progress"
                    />
                    <span className="active-days__value">{value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="activity-map__detail-col">
          <h2 className="activity-map__section-title">
            {intl.formatMessage(messages.monthlySummary)}
          </h2>
          <div className="activity-map__card">
            <ul className="monthly-summary__list">
              {monthlyRows.map((row) => {
                const isImprovement = row.deltaPercent != null && row.deltaPercent >= 0;
                const deltaMessage = row.deltaPercent == null
                  ? null
                  : intl.formatMessage(
                    isImprovement ? messages.improvementBy : messages.declineBy,
                    { percent: Math.abs(row.deltaPercent) },
                  );
                const popover = (
                  <Popover id={`monthly-popover-${row.key}`} className="monthly-summary__popover">
                    <Popover.Content>
                      <div className="monthly-summary__popover-title">{row.name}</div>
                      <div className="monthly-summary__popover-body">
                        <div className="monthly-summary__popover-stats">
                          <div className="monthly-summary__stat">
                            <span className="monthly-summary__stat-label">
                              {intl.formatMessage(messages.activeDays)}
                            </span>
                            <span className="monthly-summary__stat-value">{row.activeDays}</span>
                          </div>
                          <div className="monthly-summary__stat">
                            <span className="monthly-summary__stat-label">
                              {intl.formatMessage(messages.unitsCompleted)}
                            </span>
                            <span className="monthly-summary__stat-value">{row.units}</span>
                          </div>
                        </div>
                        {deltaMessage && (
                          <div
                            className={`monthly-summary__delta monthly-summary__delta--${isImprovement ? 'up' : 'down'}`}
                          >
                            <RiLineChartLine size={16} />
                            <span>{deltaMessage}</span>
                          </div>
                        )}
                      </div>
                    </Popover.Content>
                  </Popover>
                );
                return (
                  <OverlayTrigger
                    key={row.key}
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popover}
                    popperConfig={{
                      modifiers: [
                        { name: 'offset', options: { offset: [0, -24] } },
                      ],
                    }}
                  >
                    <li className="monthly-summary__row" tabIndex={0}>
                      <span className="monthly-summary__name">
                        {row.name}
                        {row.isCurrent && (
                          <Badge variant="light" className="monthly-summary__badge">
                            {intl.formatMessage(messages.current)}
                          </Badge>
                        )}
                      </span>
                      <span className="monthly-summary__units">
                        {intl.formatMessage(messages.units, { count: row.units })}
                      </span>
                    </li>
                  </OverlayTrigger>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedActivityMap;
